import { useTheme, themes } from '../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Palette, Moon, Skull, Sparkles, Radio, Eye } from 'lucide-react';

// Icon mapping for each theme
const themeIcons = {
  original: Palette,
  cosmicHorror: Skull,
  alienEncounter: Sparkles,
  deepNebula: Moon,
  ufoHunter: Radio,
  voidWalker: Eye
};

const ThemeSelector = ({ isOpen, onClose }) => {
  const { currentTheme, changeTheme, themeList } = useTheme();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="glass-panel max-w-4xl w-full p-6 my-8"
          onClick={(e) => e.stopPropagation()}
          data-testid="theme-selector-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-unbounded font-bold text-2xl flex items-center gap-3" style={{ color: 'var(--theme-text)' }}>
              <Palette className="w-7 h-7" style={{ color: 'var(--theme-primary)' }} />
              Theme Selector
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              style={{ color: 'var(--theme-text-muted)' }}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <p className="font-exo2 mb-6" style={{ color: 'var(--theme-text-muted)' }}>
            Choose a theme to customize the look and feel of Skybot. Each theme has unique colors, fonts, and visual effects.
          </p>

          {/* Theme Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {themeList.map((theme) => {
              const Icon = themeIcons[theme.id] || Palette;
              const isActive = currentTheme === theme.id;
              
              return (
                <motion.div
                  key={theme.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => changeTheme(theme.id)}
                  className="relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
                  style={{
                    background: theme.colors.surface,
                    border: `2px solid ${isActive ? theme.colors.accent : theme.colors.border}`,
                    boxShadow: isActive ? theme.effects.glow : 'none'
                  }}
                  data-testid={`theme-${theme.id}`}
                >
                  {/* Background Image Preview */}
                  <div 
                    className="h-20 relative"
                    style={{
                      backgroundImage: `url('${theme.background}')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent, ${theme.colors.surface})` }} />
                    {/* Color Bar overlay */}
                    <div 
                      className="absolute bottom-0 left-0 right-0 h-1"
                      style={{
                        background: `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.secondary}, ${theme.colors.accent})`
                      }}
                    />
                  </div>

                  <div className="p-4">
                    {/* Theme Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ background: theme.colors.glass, border: `1px solid ${theme.colors.border}` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: theme.colors.primary }} />
                        </div>
                        <div>
                          <h3 
                            className="font-bold text-lg"
                            style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}
                          >
                            {theme.name}
                          </h3>
                        </div>
                      </div>
                      
                      {isActive && (
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ background: theme.colors.accent }}
                        >
                          <Check className="w-4 h-4" style={{ color: theme.colors.void }} />
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <p 
                      className="text-sm mb-4"
                      style={{ fontFamily: theme.fonts.body, color: theme.colors.textMuted }}
                    >
                      {theme.description}
                    </p>

                    {/* Color Swatches */}
                    <div className="flex gap-2">
                      {[theme.colors.primary, theme.colors.secondary, theme.colors.accent, theme.colors.danger].map((color, i) => (
                        <div
                          key={i}
                          className="w-6 h-6 rounded-full border border-white/20"
                          style={{ background: color }}
                          title={color}
                        />
                      ))}
                    </div>

                    {/* Font Preview */}
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: theme.colors.border }}>
                      <div className="flex items-center gap-2 text-xs" style={{ color: theme.colors.textMuted }}>
                        <span style={{ fontFamily: theme.fonts.heading }}>Heading</span>
                        <span>•</span>
                        <span style={{ fontFamily: theme.fonts.body }}>Body</span>
                        <span>•</span>
                        <span style={{ fontFamily: theme.fonts.mono }}>Mono</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Current Theme Info */}
          <div 
            className="mt-6 p-4 rounded-xl"
            style={{ 
              background: 'var(--theme-glass)',
              border: '1px solid var(--theme-border)'
            }}
          >
            <p className="font-mono text-xs" style={{ color: 'var(--theme-text-muted)' }}>
              Current Theme: <span style={{ color: 'var(--theme-primary)' }}>{themes[currentTheme]?.name}</span>
              {' '} • Theme settings are saved locally
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ThemeSelector;
